/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useCallback } from 'react'
import { Drawer, Button, InputNumber, Card, Input, Upload, message, Tooltip, Progress, Alert } from 'antd'
import { PlusOutlined, WarningOutlined } from '@ant-design/icons'
import InnerImageZoom from 'react-inner-image-zoom'
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.min.css'
import LazyLoad from 'react-lazy-load'
import Moment from 'react-moment'
import { Trash } from '@mercadoni/gungnir'
import Icons from '@commonComponents/Icons'
import { formatMoney } from 'accounting-js'
import { standardDate } from '../../flag-management.tools'

import { getInvoices, downloadInvoices, updatePaymentInfo, getUploadInvoiceUrl } from '../../flag-management.services'
import { Can } from '../../../../refactory/modules/access-control/infrastructure/roles-permissions/access-control'
import { SubModulePerms } from '../../../../refactory/modules/access-control/domain/module'

const PricesTypes = {
  ORDER_VALUE: 'order_value',
  SHIPPING_FEE: 'shipping_fee',
  TAXES: 'taxes',
  DISCOUNTS: 'discounts',
  SUBTOTAL: 'subtotal'
}

const validTypes = [
  'image/jpeg',
  'image/png',
  'image/jpg',
  'text/plain',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
  'application/vnd.ms-excel'
]

const getBase64 = (img, callback) => {
  const reader = new FileReader()
  reader.addEventListener('load', () => callback(reader.result))
  reader.readAsDataURL(img)
}

const progressColor = {
  '0%': '#3657BB',
  '100%': '#9E3434'
}

const Invoices = ({
  copy,
  handleView,
  visible,
  invoices,
  jobId,
  jobNumber,
  checkoutInfo,
  timeZone,
  updatedPrices,
  symbolValue,
  setSelectedJob,
  jobsSearch,
  user,
  paymentInfo = {}
}) => {
  const [, updateState] = useState()
  const [photos, setPhotos] = useState()
  const [files, setFiles] = useState()
  const [selectedInvoice, setSelectedInvoice] = useState()
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [showEditValues, setShowEditValues] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [uploadFileProgress, setUploadFileProgress] = useState(0)
  const [uploadingFile, setUploadingFile] = useState(null)
  const [reload, setReload] = useState(false)
  const [isMount, setIsMount] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [newPrices, setNewPrices] = useState({
    taxes: 0,
    subtotal: 0,
    discounts: 0,
    shippingFee: 0,
    orderValue: 0,
    attributes: []
  })
  const forceRender = useCallback(() => updateState({}), [])
  const styleForTransactionId = {
    fontSize: '1.2rem',
    color: 'var(--character-secondary-45, rgba(0, 0, 0, 0.45))',
    paddingTop: '12px'
  }
  const beforeUpload = async (file) => {
    const isAValidType = validTypes.includes(file.type)
    if (!isAValidType) {
      message.error(copy('youCanOnlyUploadTextOrImageFiles'))
    }
    const isLt5M = file.size / 1024 / 1024 < 5
    if (!isLt5M) {
      message.error(copy('fileMustSmallerThan2MB'))
    }
    const isGreaterThanZero = file.size > 0
    if (!isGreaterThanZero) {
      message.error(copy('fileMustBeGreaterThanZero'))
    }
    return isAValidType && isLt5M && isGreaterThanZero
  }

  const cleanUploadStates = () => {
    setUploadingFile(null)
    setUploadLoading(false)
    setUploadFileProgress(0)
    setShowAlert(false)
  }

  const uploadFile = async (data) => {
    const fileName = data?.file?.name
    const fileType = data?.file?.type
    setUploadLoading(true)
    getBase64(data?.file, async (fileUrl) => {
      try {
        setUploadingFile({ fileName, fileType: fileName?.split('.')?.[1], fileUrl })
        setUploadFileProgress(20)
        const result = await getUploadInvoiceUrl(jobId, fileName, fileType)
        if (result) {
          const { url, finalUrl } = result
          const preFile = await fetch(fileUrl)
          const image = await preFile.blob()
          setUploadFileProgress(30)
          const myHeaders = new Headers()
          myHeaders.append('Content-Type', fileType)
          const response = await fetch(url, {
            method: 'PUT',
            headers: myHeaders,
            body: image,
            redirect: 'follow'
          })
          setUploadFileProgress(60)
          if (response?.ok) {
            const { invoice = {} } = paymentInfo
            const attachments = invoice?.attachments || []
            const payload = { ...invoice, attachments: [...attachments, finalUrl] }
            if (!payload?.reference) {
              payload.reference = ''
            }
            const updatedJob = await updatePaymentInfo(jobId, { invoice: payload })
            setUploadFileProgress(100)
            setReload(true)
            if (jobsSearch) setSelectedJob(updatedJob)
            message.success(copy('fileUploadedSuccessfully'))
          } else {
            message.error(copy('errorUploadingFile'))
          }
          cleanUploadStates()
        }
      } catch (error) {
        message.error(copy('errorUploadingFile'))
        console.error('Uploading file error', error)
        cleanUploadStates()
      }
    })
  }

  const download = () => {
    try {
      setDownloadLoading(true)
      downloadInvoices(jobId).then((result) => {
        result.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `invoices-${jobNumber}.zip`
          a.click()
          setDownloadLoading(false)
        })
      })
    } catch (error) {
      setDownloadLoading(false)
    }
  }

  const isSelected = (photo) => {
    return photo === selectedInvoice
  }

  const cleanStates = () => {
    setIsMount(false)
    setPhotos(null)
    setFiles(null)
    setSelectedInvoice(null)
    setShowEditValues(false)
    setDownloadLoading(false)
    setSaveLoading(false)
    setNewPrices({
      subtotal: 0,
      shippingFee: 0,
      discounts: 0,
      taxes: 0,
      orderValue: 0,
      attributes: []
    })
    setShowAlert(false)
  }

  const getSumTotalValue = (prices) => {
    let total = 0
    Object.keys(prices).forEach((pricesKey) => {
      if (pricesKey !== 'attributes' && pricesKey !== 'orderValue') {
        total += pricesKey === 'discounts' ? -1 * prices[pricesKey] : prices[pricesKey]
      }
    })
    return total
  }

  const handleChangePrices = (fieldName, value, _id) => {
    let _newPrices = newPrices
    if (!_id) {
      _newPrices = { ...newPrices, [fieldName]: value }
    } else {
      const attrIndex = newPrices?.attributes?.findIndex(({ id }) => id === _id)
      if (attrIndex >= 0) {
        _newPrices.attributes[attrIndex].value = value
      }
    }
    if (fieldName !== 'orderValue') {
      _newPrices.orderValue = getSumTotalValue(_newPrices)
    }
    setNewPrices(_newPrices)
    forceRender()
  }

  const handleChangeAttributeName = (_id, newName) => {
    const _newPrices = newPrices
    const attrIndex = newPrices?.attributes?.findIndex(({ id }) => id === _id)
    _newPrices.attributes[attrIndex].name = newName
    setNewPrices(_newPrices)
    forceRender()
  }

  const getAttributeValue = (attributes, _type, _id) => {
    const attribute = attributes?.find(({ id }) => id === _id)
    return attribute?.value || 0
  }

  const editFields = (name, invoiceValue, id = null) => {
    const negativeSign = name === 'discounts' && invoiceValue > 0 ? '- ' : ''
    const _value = id !== null ? getAttributeValue(newPrices?.attributes, name, id) : newPrices[name]
    const inputStyle = id === null && name !== 'orderValue' ? { marginRight: '30px' } : {}
    return (
      <div>
        {showEditValues ? (
          <InputNumber
            onChange={(value) => handleChangePrices(name, value, id)}
            value={_value}
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => value.replace(/\\s?|(,*)/g, '')}
            className='edit-invoice-values'
            style={inputStyle}
            max={100000000000}
          />
        ) : (
          <span>
            {negativeSign}
            {formatMoney(invoiceValue, symbolValue)}
          </span>
        )}
      </div>
    )
  }

  const updatePaymentInfoPrices = async () => {
    try {
      setSaveLoading(true)
      const _newPrices = JSON.parse(JSON.stringify(newPrices))
      _newPrices.attributes = _newPrices.attributes.map((a) => {
        delete a.id
        return a
      })
      const updatedJob = await updatePaymentInfo(jobId, { prices: _newPrices })
      if (jobsSearch) setSelectedJob(updatedJob)
      setSaveLoading(false)
      setShowEditValues(false)
      setShowAlert(true)
    } catch (error) {
      setSaveLoading(false)
      setShowEditValues(false)
    }
  }

  const removeAttribute = (_id) => {
    const _newPrices = newPrices
    const attrIndex = newPrices?.attributes?.findIndex(({ id }) => id === _id)
    _newPrices.attributes?.splice(attrIndex, 1)
    setNewPrices(_newPrices)
    forceRender()
  }

  const getAttributesByType = (type = '', attributes = []) => {
    const mainClass = 'payment-summary-body-section-attributes'
    if (attributes.length === 0) {
      return null
    }
    const attributesByType = attributes.filter(({ type: _type }) => type === _type.toLowerCase())
    if (!attributesByType || attributesByType.length === 0) {
      return null
    }
    return (
      <div className={mainClass}>
        {attributesByType.map(({ id, name, value }, idx) => {
          return (
            <dl key={`pybs-${idx}`} className={`${mainClass}-item`} style={showEditValues ? { marginTop: '8px' } : {}}>
              <dt>
                {showEditValues ? (
                  <Input
                    value={name || ''}
                    onChange={({ target: { value: _value } }) => handleChangeAttributeName(id, _value)}
                  />
                ) : (
                  name
                )}
              </dt>
              <dd className={`${mainClass}-item-value`}>
                {editFields(type, value, id)}
                {showEditValues && <Trash onClick={() => removeAttribute(id)} />}
              </dd>
            </dl>
          )
        })}
      </div>
    )
  }

  const addNewAttribute = (type) => {
    const lastAttr = newPrices?.attributes?.length > 0 ? newPrices?.attributes?.[newPrices?.attributes?.length - 1] : {}
    const _newPrices = newPrices
    _newPrices?.attributes?.push({
      id: lastAttr?.id + 1 || 1,
      type: type.toUpperCase(),
      name: '',
      value: 0
    })
    setNewPrices(_newPrices)
    forceRender()
  }

  const getAddButton = (type) => {
    const mainClass = 'payment-summary'
    if (!showEditValues) return null
    return (
      <div className={`${mainClass}-body-add-button`}>
        <Button type='link' onClick={() => addNewAttribute(type)}>
          <u>{copy(`addNew-${type}`)}</u>
          {' +'}
        </Button>
      </div>
    )
  }

  const showPaymentMethodTextAndIcon = (method) => {
    switch (method) {
      case 'CASH':
        return <Icons iconName='PaymentMethodCash' />
      case 'REPAID':
        return <Icons iconName='PaymentMethodCreditCard' />
      case 'PAYMENT_LINK':
        return <Icons iconName='PaymentMethodPaymentLink' />
      case 'PAYMENT_TERMINAL':
        return <Icons iconName='PaymentMethodPaymentTerminal' />
      case 'TRANSFER':
        return <Icons iconName='PaymentMethodTransfer' />
      case 'LOYALTY_CARD':
        return <Icons iconName='PaymentMethodLoyaltyCard' />
      default:
        break
    }
  }

  const showPaymentStatusTextAndIcon = (status) => {
    switch (status) {
      case 'succeeded':
        return <Icons iconName='PaymentStatusSuccess' />
      case 'failed':
        return <Icons iconName='PaymentStatusFailed' />
      case 'changed':
        return <Icons iconName='PaymentStatusChanged' />
      case 'reverted':
        return <Icons iconName='PaymentStatusReverted' />
      case 'in_progress':
        return <Icons iconName='PaymentStatusInProgress' />
      case 'pending':
        return <Icons iconName='PaymentStatusPending' />
      default:
        break
    }
  }

  const renderAdjustmentSection = (item, mainClass) => {
    return (
      <div className={`${mainClass}-body-section`} style={{ borderTop: 'solid 1px #d9dbdd' }}>
        <dl>
          <dt className={`${mainClass}-body-section-title`}>{`${copy(item.type)}:`}</dt>
          <dd className={`${mainClass}-body-section-title`}>{formatMoney(item.value, symbolValue)}</dd>
        </dl>
      </div>
    )
  }

  const renderOriginalTotalValueSection = (item, mainClass) => {
    return (
      <div className={`${mainClass}-total`}>
        <span>{copy(item.type)}</span>
        <span style={{ fontWeight: '600' }}>{formatMoney(item.value, symbolValue)}</span>
      </div>
    )
  }

  const renderAjustmentStatusIcon = (adjustmentItem) => {
    if (!adjustmentItem) {
      return <></>
    }

    const iconComponent =
      adjustmentItem.value < 0 ? (
        <Icons iconName='ArrowRedDown' style={{ verticalAlign: 'middle', marginRight: '3px' }} />
      ) : adjustmentItem.value > 0 ? (
        <Icons iconName='ArrowGreeUp' style={{ verticalAlign: 'middle', marginRight: '3px' }} />
      ) : (
        <Icons iconName='EqualsInvoice' style={{ verticalAlign: 'middle', marginRight: '3px' }} />
      )

    return iconComponent
  }

  const getInvoiceValues = () => {
    const additionalInfo = paymentInfo?.prices.additionalInfo
    let adjustmentItem = null
    if (additionalInfo && additionalInfo.length > 0) {
      adjustmentItem = additionalInfo.find((item) => item.type === 'adjustment')
      additionalInfo.sort((a, b) => (a.type > b.type ? -1 : 1))
    }

    const subtotal =
      newPrices?.subtotal !== updatedPrices?.subtotal ? newPrices?.subtotal : updatedPrices?.subtotal || 0
    const shippingFee =
      newPrices?.shippingFee !== updatedPrices?.shippingFee ? newPrices?.shippingFee : updatedPrices?.shippingFee || 0
    const taxes = newPrices?.taxes !== updatedPrices?.taxes ? newPrices?.taxes : updatedPrices?.taxes || 0
    const discounts =
      newPrices?.discounts !== updatedPrices?.discounts ? newPrices?.discounts : updatedPrices?.discounts || 0
    const orderValue =
      newPrices?.orderValue !== updatedPrices?.orderValue ? newPrices?.orderValue : updatedPrices?.orderValue || 0
    const attributes = newPrices?.attributes || []
    const mainClass = 'payment-summary'
    const isValidDiscounts = !!(discounts && !isNaN(discounts))
    const editLink = Can([SubModulePerms.JOB_GENERAL_INVOICE_EDIT]) ? (
      <Button type='link' onClick={() => setShowEditValues(!showEditValues)} disabled={showEditValues}>
        <u>{copy('editSummary')}</u>
      </Button>
    ) : null
    return (
      <Card className={mainClass} title={copy('paymentSummary')} extra={editLink}>
        <div className={`${mainClass}-body`}>
          <div className={`${mainClass}-body-section`}>
            <dl>
              <dt className={`${mainClass}-body-section-title`}>{`${copy('subtotal')}:`}</dt>
              <dd className={`${mainClass}-body-section-title`}>{editFields('subtotal', subtotal)}</dd>
            </dl>
            {getAttributesByType(PricesTypes.SUBTOTAL, attributes)}
          </div>
          {getAddButton(PricesTypes.SUBTOTAL)}
          <div className={`${mainClass}-body-section`}>
            <dl>
              <dt className={`${mainClass}-body-section-title`}>{`${copy('shippingFee')}:`}</dt>
              <dd className={`${mainClass}-body-section-title`}>{editFields('shippingFee', shippingFee)}</dd>
            </dl>
            {getAttributesByType(PricesTypes.SHIPPING_FEE, attributes)}
          </div>
          {getAddButton(PricesTypes.SHIPPING_FEE)}
          {(taxes > 0 || showEditValues) && (
            <div className={`${mainClass}-body-section`}>
              <dl>
                <dt className={`${mainClass}-body-section-title`}>{`${copy('taxes')}:`}</dt>
                <dd className={`${mainClass}-body-section-title`}>{editFields('taxes', taxes)}</dd>
              </dl>
              {getAttributesByType(PricesTypes.TAXES, attributes)}
            </div>
          )}
          {getAddButton(PricesTypes.TAXES)}
          {(isValidDiscounts || showEditValues) && (
            <div className={`${mainClass}-body-section`}>
              <dl>
                <dt className={`${mainClass}-body-section-title`}>{`${copy('discounts')}:`}</dt>
                <dd className='red-price'>{editFields('discounts', discounts)}</dd>
              </dl>
              {getAttributesByType(PricesTypes.DISCOUNTS, attributes)}
            </div>
          )}
          {getAddButton(PricesTypes.DISCOUNTS)}
        </div>
        {additionalInfo &&
          additionalInfo.map((item) =>
            item.type === 'adjustment'
              ? renderAdjustmentSection(item, mainClass)
              : renderOriginalTotalValueSection(item, mainClass, additionalInfo)
          )}
        <div className={`${mainClass}-total`}>
          <span>{copy('totalToPay')}</span>
          <span style={{ fontWeight: '600' }}>
            {renderAjustmentStatusIcon(adjustmentItem)}
            <span>{formatMoney(orderValue, symbolValue)}</span>
          </span>
        </div>
        {showEditValues && (
          <div className={`${mainClass}-actions`}>
            <Button
              onClick={() => {
                mapPricesToNewPrices()
                setShowEditValues(false)
              }}
            >
              {copy('discard')}
            </Button>
            <Button type='primary' loading={saveLoading} onClick={updatePaymentInfoPrices}>
              {copy('saveChanges')}
            </Button>
          </div>
        )}
      </Card>
    )
  }
  const onCopyTransactionId = (transactionId) => {
    navigator.clipboard.writeText(transactionId)
  }

  const renderChargeSection = (item, mainClass) => {
    return (
      <div className={`${mainClass}-body-section`}>
        <dl>
          <dt className={`${mainClass}-body-section-title`}>
            <span style={{ display: 'block' }}>
              {`${copy(item.type)} `}
              <span style={{ fontSize: '1.3rem', color: 'var(--character-secondary-45, rgba(0, 0, 0, 0.45))' }}>
                <Moment format='LLL'>{standardDate(item.created_at)}</Moment>
              </span>
            </span>
            <span style={styleForTransactionId}>
              Id: {item.transaction_id}{' '}
              <Icons iconName='Copy' onClick={() => onCopyTransactionId(`${item.transaction_id}`)} />
            </span>
          </dt>
          <dd className={`${mainClass}-body-section-title`}>
            <span style={{ display: 'block', paddingTop: '12px' }}>{formatMoney(item.amount_value, symbolValue)}</span>
          </dd>
        </dl>
      </div>
    )
  }

  const renderRefundSection = (item, mainClass) => {
    return (
      <div className={`${mainClass}-body-section`}>
        <dl>
          <dt className={`${mainClass}-body-section-title`}>
            <span style={{ display: 'block', paddingTop: '8px', paddingBottom: '8px' }}>
              {`${copy(item.type)}`} {renderTransactionStatusTag(item)}
            </span>
            <span style={styleForTransactionId}>
              Id: {item.transaction_id}{' '}
              <Icons iconName='Copy' onClick={() => onCopyTransactionId(`${item.transaction_id}`)} alt='copy' />
            </span>
          </dt>
          <dd className={`${mainClass}-body-section-title`}>
            <span style={{ display: 'block', paddingTop: '12px' }}>{formatMoney(item.amount_value, symbolValue)}</span>
          </dd>
        </dl>
      </div>
    )
  }

  const renderTransactionStatusTag = (item) => {
    const label = copy(item.status)
    const iconComponent =
      item.status === 'DECLINED' ? (
        <Icons iconName='WarningRedInvoice' style={{ marginRight: '3px' }} />
      ) : item.status === 'COMPLETED' ? (
        <Icons iconName='CheckGreenInvoice' style={{ marginRight: '3px' }} />
      ) : (
        <Icons iconName='LoadedInvoice' style={{ marginRight: '3px' }} />
      )
    return (
      <div
        className={[
          item.status === 'DECLINED'
            ? 'transaction-error-tag'
            : item.status === 'COMPLETED'
            ? 'transaction-success-tag'
            : 'transaction-pending-tag'
        ]}
      >
        <label>
          {iconComponent}
          {label}
        </label>
      </div>
    )
  }

  const getOrderValues = () => {
    const metadata = paymentInfo?.payment.metadata
    const mainClass = 'payment-summary'
    let orderValue = 0

    if (!metadata || !metadata.transaction) {
      return <></>
    }

    metadata.transaction.forEach((item) => {
      if (item.type === 'CHARGE' || (item.type === 'REFUND' && item.status === 'COMPLETED')) {
        if (item.type === 'REFUND') {
          orderValue = orderValue - item.amount_value
        } else {
          orderValue = orderValue + item.amount_value
        }
      }
    })
    return (
      <Card className={mainClass} title={copy('movementDetail')}>
        <div className={`${mainClass}-body`}>
          {metadata.transaction.map((item) =>
            item.type === 'CHARGE' ? renderChargeSection(item, mainClass) : renderRefundSection(item, mainClass)
          )}
          <div className={`${mainClass}-total`} style={{ padding: '16px', paddingLeft: '20px' }}>
            <span>{copy('paymentValue')}</span>
            <span>{formatMoney(orderValue, symbolValue)}</span>
          </div>
        </div>
      </Card>
    )
  }

  const getInvoiceHeader = () => {
    const tooltipTextPaymentMethod = copy(paymentInfo?.payment?.method)
    const textPaymentMethod = paymentInfo?.payment?.methodDetails
      ? paymentInfo?.payment?.methodDetails
      : tooltipTextPaymentMethod
    const paymentStatus = paymentInfo?.payment?.paymentStatus
      ? paymentInfo?.payment?.paymentStatus.toLowerCase()
      : 'pending'
    const classPaymentStatus = `payment-status-${paymentStatus}`
    const alertMessage = (
      <Alert
        description={
          <div className='invoice-header-alert'>
            <div className='invoice-header-alert-icon'>
              <WarningOutlined />
            </div>
            <div className='invoice-header-alert-message'>
              <b>{copy('theOrderDetailsHaveChanged')}</b>
              <span>{copy('rememberThatYouMustUpdateThePhotoOfTheInvoice')}</span>
            </div>
          </div>
        }
        type='error'
      />
    )
    return (
      <Card className='invoice-header'>
        {showAlert && alertMessage}
        <dl className='invoice-header-fields'>
          <dt>{copy('jobs.job.info.invoices.invoice')}</dt>
          <dd>{paymentInfo?.invoice?.reference || invoices?.billingNumber || ''}</dd>
        </dl>
        <dl className='invoice-header-fields'>
          <dt>{copy('invoiced')}</dt>
          <dd>
            {checkoutInfo?.actualEnd ? (
              <Moment format='LL' tz={timeZone}>
                {standardDate(checkoutInfo.actualEnd)}
              </Moment>
            ) : null}
          </dd>
        </dl>
        <dl className='invoice-header-fields'>
          <dt>{copy('paymentMethod')}</dt>
          <dd>
            <label>
              {textPaymentMethod}{' '}
              <Tooltip placement='right' title={tooltipTextPaymentMethod}>
                <span>{showPaymentMethodTextAndIcon(paymentInfo?.payment?.method)}</span>
              </Tooltip>
            </label>
          </dd>
        </dl>
        <dl className='invoice-header-fields'>
          <dt>{copy('paymentStatus')}</dt>
          <dd>
            <label className={classPaymentStatus}>
              {copy(paymentStatus)} {showPaymentStatusTextAndIcon(paymentStatus)}
            </label>
          </dd>
        </dl>
        <dl className='invoice-header-fields'>
          <dt>{copy('paymentStatusDetail')}</dt>
          <dd>
            <label>
              {paymentInfo?.payment.paymentStatusDetails
                ? paymentInfo?.payment.paymentStatusDetails
                : copy('empty-text')}
            </label>
          </dd>
        </dl>
      </Card>
    )
  }

  const uploadButton = uploadLoading ? (
    <Progress width={80} type='circle' strokeColor={progressColor} percent={uploadFileProgress} />
  ) : (
    <div className='upload-button'>
      <PlusOutlined />
    </div>
  )

  const uploadCard = () => {
    return (
      <div className='upload-card'>
        <Tooltip placement='top' title={copy('addFile')}>
          <Upload
            name='avatar'
            listType='picture-card'
            className='file-uploader'
            showUploadList={false}
            customRequest={uploadFile}
            beforeUpload={beforeUpload}
            disabled={uploadLoading}
          >
            {uploadButton}
          </Upload>
        </Tooltip>
      </div>
    )
  }

  const getFileProperties = (file) => {
    const splittedFile = file?.split('/')
    const name = splittedFile?.[splittedFile.length - 1]?.split('?')?.[0]
    const type = name?.split('.')?.[1]?.toLowerCase()
    return {
      name,
      imageUrl: type === 'pdf' ? '/assets/pdf_icon.png' : '/assets/file.png',
      type
    }
  }

  const getSelectedFile = () => {
    const validTextFiles = {
      pdf: 'application/pdf',
      txt: 'text/plain',
      csv: 'text/csv'
    }
    const { type } = getFileProperties(selectedInvoice)
    let showFile = null
    if (selectedInvoice) {
      if (['png', 'jpeg', 'jpg'].includes(type)) {
        showFile = (
          <LazyLoad>
            <InnerImageZoom src={selectedInvoice} moveType='drag' />
          </LazyLoad>
        )
      } else if (validTextFiles[type]) {
        showFile = <embed src={selectedInvoice} type={validTextFiles[type]} className='pdf-invoices' />
      } else {
        showFile = (
          <div className='no-supported-file'>
            <span>{copy('notSupportedFile')}</span>
          </div>
        )
      }
    }
    return <div className='selected-invoice'>{showFile}</div>
  }

  const uploadingCard = () => {
    const mainClass = 'payment-summary'
    if (!uploadingFile) return null
    const { fileName, fileType, fileUrl } = uploadingFile
    const imageUrl = ['png', 'jpeg', 'jpg'].includes(fileType)
      ? fileUrl
      : fileType === 'pdf'
      ? '/assets/pdf_icon.png'
      : '/assets/file.png'
    return (
      <div className={`${mainClass}-body-uploading-card`}>
        <div className={`${mainClass}-body-uploading-card-image`}>
          <LazyLoad>
            <img src={imageUrl} alt={fileName} />
          </LazyLoad>
        </div>
        <div className={`${mainClass}-body-uploading-card-progress`}>
          <span>{fileName}</span>
          <Progress strokeColor={progressColor} percent={uploadFileProgress} />
        </div>
      </div>
    )
  }

  const getInvoiceSection = () => {
    const mainClass = 'payment-summary'
    const editLink = Can([SubModulePerms.JOB_GENERAL_INVOICE_UPLOAD]) ? (
      <Upload
        name='file'
        showUploadList={false}
        customRequest={uploadFile}
        beforeUpload={beforeUpload}
        disabled={uploadLoading}
      >
        <Button type='link' disabled={uploadLoading}>
          <u>{copy('uploadNewInvoice')}</u>
        </Button>
      </Upload>
    ) : null
    return (
      <Card className={mainClass} title={copy('orderInvoice')} extra={editLink}>
        <div className={`${mainClass}-body`}>
          {uploadingCard()}
          {!!photos || !!files ? (
            <div className={`${mainClass}-body-invoices`}>
              <div className={`${mainClass}-body-invoices-container`}>
                <div className='invoices-list'>
                  {photos?.map((photo, key) => {
                    return (
                      <div className={`file-image ${isSelected(photo) ? 'image-selected' : ''}`} key={key}>
                        <LazyLoad>
                          <img onClick={() => setSelectedInvoice(photo)} src={photo} alt={`invoice-${key}`} />
                        </LazyLoad>
                      </div>
                    )
                  })}
                  {files?.map((file, key) => {
                    const { name, imageUrl } = getFileProperties(file)
                    return (
                      <div className={`file-image ${isSelected(file) ? 'image-selected' : ''}`} key={key}>
                        <LazyLoad>
                          <img onClick={() => setSelectedInvoice(file)} src={imageUrl} alt={`invoice-${key}`} />
                        </LazyLoad>
                        <span>{name}</span>
                      </div>
                    )
                  })}
                  {Can([SubModulePerms.JOB_GENERAL_INVOICE_UPLOAD]) && uploadCard()}
                </div>
                {getSelectedFile()}
              </div>
            </div>
          ) : (
            <div className={`${mainClass}-body-no-invoice-container`}>
              {Can([SubModulePerms.JOB_GENERAL_INVOICE_UPLOAD]) && uploadCard()}
              <div className='info'>
                <div className='header'>{copy('jobs.job.info.invoices.no-invoice.title')}</div>
                <div className='message'>{copy('jobs.job.info.invoices.no-invoice.message')}</div>
              </div>
            </div>
          )}
          <div className={`${mainClass}-body-invoices-actions`}>
            <div className='responsable'>
              <b>{copy('jobs.jobCancel.form.responsible')}: </b>
              <span>
                {user.firstName} {user.lastName}
              </span>
            </div>
            <Button
              className='download-button'
              id='download-invoices'
              type='primary'
              onClick={download}
              loading={downloadLoading}
              disabled={!photos && !files}
            >
              {copy('downloadInvoices')}
            </Button>
          </div>
        </div>
      </Card>
    )
  }

  const mapPricesToNewPrices = () => {
    const subtotal =
      paymentInfo?.prices?.subtotal !== updatedPrices?.subtotal
        ? paymentInfo?.prices?.subtotal
        : updatedPrices?.subtotal || 0
    const shippingFee =
      paymentInfo?.prices?.shippingFee !== updatedPrices?.shippingFee
        ? paymentInfo?.prices?.shippingFee
        : updatedPrices?.shippingFee || 0
    const discounts =
      paymentInfo?.prices?.discounts !== updatedPrices?.discounts
        ? paymentInfo?.prices?.discounts
        : updatedPrices?.discounts || 0
    const taxes =
      paymentInfo?.prices?.taxes !== updatedPrices?.taxes ? paymentInfo?.prices?.taxes : updatedPrices?.taxes || 0
    const orderValue =
      paymentInfo?.prices?.orderValue !== updatedPrices?.orderValue
        ? paymentInfo?.prices?.orderValue
        : updatedPrices?.orderValue || 0
    const totalOrder = paymentInfo?.payment?.value || 0

    const _newPrices = {
      subtotal: subtotal,
      shippingFee: shippingFee,
      discounts: discounts,
      taxes: taxes,
      orderValue: orderValue,
      totalOrder: totalOrder,
      attributes:
        paymentInfo?.prices?.attributes.map((a, idx) => {
          a.id = idx + 1
          return a
        }) || []
    }
    setNewPrices(_newPrices)
  }

  const getURLs = async (_) => {
    const photosUrls = []
    const filesUrls = []
    const invoicesURL = await getInvoices(jobId)
    if (invoicesURL && invoicesURL.length > 0) {
      invoicesURL.forEach((invoiceUrl) => {
        const url = invoiceUrl.toLowerCase()
        if (url.includes('.jpg') || url.includes('.png') || url.includes('.jpeg')) {
          photosUrls.push(invoiceUrl)
        } else {
          filesUrls.push(invoiceUrl)
        }
      })
      setFiles(filesUrls.length > 0 ? filesUrls : null)
      setPhotos(photosUrls.length > 0 ? photosUrls : null)
      setSelectedInvoice(photosUrls[0])
      setReload(false)
    }
  }

  useEffect(
    (_) => {
      if (visible) {
        setIsMount(true)
        mapPricesToNewPrices()

        if (((paymentInfo?.invoice?.attachments || invoices?.photosUrl) && !photos && !files) || reload) {
          getURLs()
        }
      }
      if (!visible && isMount) {
        cleanStates()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, paymentInfo?.prices, paymentInfo?.invoice?.attachments, photos, jobId]
  )

  return (
    <Drawer
      className='invoices-drawer'
      title={copy('jobs.job.info.invoices.title')}
      width={670}
      onClose={handleView}
      visible={visible}
    >
      <div>
        {getInvoiceHeader()}
        {getInvoiceValues()}
        {getOrderValues()}
        {getInvoiceSection()}
      </div>
    </Drawer>
  )
}

export default Invoices
